import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Collapse,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { normalizePossibleMatches } from '../utils/deriveJoinsFromMappings';

const stripTable = (s) => (s && s.includes('.') ? s.slice(s.lastIndexOf('.') + 1) : s || '');

const JoinCard = ({
  mapping,
  editIndex,
  disabled = false,
  onEdit,
  onDelete,
  onOpenReview,
  showEditDelete = false,
  sectionKey = '',
}) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  if (!mapping || typeof mapping !== 'object') return null;

  const mappingType = (mapping.mapping_type || 'DIRECT').toUpperCase();
  const sourceColumns = Array.isArray(mapping.source_columns) ? mapping.source_columns : [];
  const sourceFull = sourceColumns[0] || '';
  const secondFull = sourceColumns[1] || '';
  const left = sourceFull ? (sourceFull.includes('.') ? sourceFull.slice(0, sourceFull.indexOf('.')) : sourceFull) : '';
  const leftKey = sourceFull
    ? sourceFull.includes('.')
      ? sourceFull.slice(sourceFull.indexOf('.') + 1)
      : sourceFull
    : '';
  const rightTableFromSecond =
    secondFull && secondFull.includes('.') ? secondFull.slice(0, secondFull.indexOf('.')) : secondFull;
  const rightKeyFromSecond =
    secondFull && secondFull.includes('.') ? secondFull.slice(secondFull.indexOf('.') + 1) : secondFull;
  const destTable = mapping.destination_name || mapping.destinationName || 'target';
  const targetColumn = mapping.target_column || mapping.targetColumn || '';
  const right = mappingType === 'JOIN' && rightTableFromSecond ? rightTableFromSecond : destTable;
  const rightKey = mappingType === 'JOIN' && rightKeyFromSecond ? rightKeyFromSecond : targetColumn;

  const isReview = mapping.mapping_state !== 'auto_merge' && mapping.mapping_state !== 'user_edited';
  const status = isReview ? 'review' : 'approved';
  const message = isReview ? 'Review required' : 'Auto-approved';

  const possibleMatchesNorm = normalizePossibleMatches(mapping.possible_matches);
  const reasoning = mapping.reasoning || '';
  const hasDetails =
    (reasoning && reasoning.length > 0) || (possibleMatchesNorm && Object.keys(possibleMatchesNorm).length > 0);
  const sourceColumnFull = sourceFull || '';
  const candidateEntries = possibleMatchesNorm ? Object.entries(possibleMatchesNorm).sort((a, b) => b[1] - a[1]) : [];

  const hasMultipleSources = sourceColumns.length > 1;
  const onClause = typeof mapping.on === 'string' ? mapping.on.trim() : '';
  const joinKeyDisplay = (() => {
    if (mappingType === 'JOIN' && onClause) {
      const eqIdx = onClause.indexOf('=');
      if (eqIdx >= 0) {
        const leftPart = onClause.slice(0, eqIdx).trim();
        const rightPart = onClause.slice(eqIdx + 1).trim();
        return { left: stripTable(leftPart), right: stripTable(rightPart), useOn: true };
      }
    }
    return { left: leftKey, right: rightKey, useOn: false };
  })();

  const handleDeleteClick = () => setDeleteDialogOpen(true);
  const handleDeleteConfirm = () => {
    onDelete && onDelete();
    setDeleteDialogOpen(false);
  };
  const handleDeleteCancel = () => setDeleteDialogOpen(false);

  const getStatusStyles = () => {
    if (status === 'approved') {
      return {
        bgcolor: '#f0fdf4',
        borderColor: '#bbf7d0',
        chipColor: 'success',
        icon: '🟢',
        borderLeft: undefined,
      };
    }
    if (status === 'review') {
      return {
        bgcolor: '#fffbeb',
        borderColor: '#fde68a',
        chipColor: 'warning',
        icon: '🟡',
        borderLeft: undefined,
      };
    }
    return {
      bgcolor: '#f9fafb',
      borderColor: '#e5e7eb',
      chipColor: 'default',
      icon: '⚪',
      borderLeft: undefined,
    };
  };

  const styles = getStatusStyles();

  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: styles.bgcolor,
        border: `1px solid ${styles.borderColor}`,
        ...(styles.borderLeft ? { borderLeft: styles.borderLeft } : {}),
        borderRadius: 2,
        p: 2,
        position: 'relative',
        overflow: 'hidden',
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
        '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
        {mappingType && (
          <Box
            component="span"
            sx={{
              px: 1.25,
              py: 0.35,
              borderRadius: 1,
              bgcolor: 'rgba(0, 4, 60, 0.08)',
              border: '1px solid rgba(0, 4, 60, 0.12)',
              fontFamily: 'inherit',
              fontSize: '0.7rem',
              fontWeight: 600,
              color: '#00043c',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            {mappingType}
          </Box>
        )}
        {showEditDelete && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              border: '1px solid',
              borderColor: 'rgba(0,0,0,0.08)',
              bgcolor: 'background.paper',
            }}
          >
            <Tooltip title="Edit mapping" placement="top" arrow>
              <span>
                <IconButton
                  size="small"
                  disabled={disabled}
                  onClick={() => onEdit?.()}
                  sx={{
                    color: 'grey.600',
                    borderRadius: 0,
                    px: 1,
                    '&:hover': {
                      color: 'primary.main',
                      bgcolor: 'rgba(25, 118, 210, 0.12)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                  aria-label="Edit mapping"
                >
                  <EditOutlinedIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Box
              sx={{
                width: '1px',
                alignSelf: 'stretch',
                minHeight: 24,
                bgcolor: 'divider',
              }}
            />
            <Tooltip title="Delete mapping" placement="top" arrow>
              <span>
                <IconButton
                  size="small"
                  disabled={disabled}
                  onClick={handleDeleteClick}
                  sx={{
                    color: 'grey.600',
                    borderRadius: 0,
                    px: 1,
                    '&:hover': {
                      color: 'error.main',
                      bgcolor: 'rgba(211, 47, 47, 0.12)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                  aria-label="Delete mapping"
                >
                  <DeleteOutlinedIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        )}
      </Box>
      {hasMultipleSources ? (
        <Box sx={{ mb: 1.25 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 0.5,
              px: 1.5,
              py: 1,
              borderRadius: 1.5,
              bgcolor: 'rgba(255,255,255,0.7)',
              border: '1px solid rgba(0, 4, 60, 0.08)',
            }}
          >
            {sourceColumns.map((src, idx) => (
              <React.Fragment key={idx}>
                {idx > 0 && (
                  <Typography component="span" sx={{ color: 'primary.main', fontWeight: 700, fontSize: '0.9rem' }}>
                    +
                  </Typography>
                )}
                <Typography component="span" sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827' }}>
                  {stripTable(src) || src}
                </Typography>
              </React.Fragment>
            ))}
            <Typography component="span" sx={{ fontWeight: 600, color: 'primary.main', mx: 0.5, fontSize: '0.9rem' }}>
              →
            </Typography>
            <Typography component="span" sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827' }}>
              {targetColumn}
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem', mt: 0.75, letterSpacing: '0.02em' }}
          >
            <Box component="span" sx={{ fontWeight: 600, mr: 0.5 }}>
              Tables:
            </Box>
            {left} ⟷ {right}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ mb: 1.25 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 0.5,
              px: 1.5,
              py: 1,
              borderRadius: 1.5,
              bgcolor: 'rgba(255,255,255,0.7)',
              border: '1px solid rgba(0, 4, 60, 0.08)',
            }}
          >
            <Typography component="span" sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827' }}>
              {joinKeyDisplay.left}
            </Typography>
            <Typography component="span" sx={{ color: 'primary.main', fontWeight: 600, fontSize: '0.85rem' }}>
              ↔
            </Typography>
            <Typography component="span" sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827' }}>
              {joinKeyDisplay.right}
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem', mt: 0.75, letterSpacing: '0.02em' }}
          >
            <Box component="span" sx={{ fontWeight: 600, mr: 0.5 }}>
              Tables:
            </Box>
            {left} ⟷ {right}
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          pt: 0.5,
          borderTop: '1px solid rgba(0,0,0,0.06)',
        }}
      >
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
          {styles.icon} {message}
        </Typography>
        {hasDetails && (
          <Button
            size="small"
            variant="text"
            disabled={disabled}
            onClick={() => setDetailsOpen((o) => !o)}
            endIcon={detailsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{
              fontSize: '0.7rem',
              textTransform: 'none',
              color: '#00043C',
              minWidth: 0,
              py: 0,
              '&:hover': { bgcolor: 'rgba(0, 4, 60, 0.06)' },
            }}
          >
            {detailsOpen ? 'Hide details' : 'Details'}
          </Button>
        )}
      </Box>

      <Collapse in={detailsOpen}>
        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          {reasoning && (
            <Box sx={{ mb: 1.5 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}
              >
                Reasoning
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: 'text.primary', lineHeight: 1.5 }}>
                {reasoning}
              </Typography>
            </Box>
          )}
          {candidateEntries.length > 0 && (
            <Box>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}
              >
                Possible matches
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                {candidateEntries.map(([col, score]) => {
                  const isMatch = sourceColumnFull && col === sourceColumnFull;
                  return (
                    <Box
                      key={col}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        fontSize: '0.7rem',
                        py: 0.25,
                        px: 1,
                        borderRadius: 0.5,
                        bgcolor: isMatch ? 'rgba(0, 4, 60, 0.1)' : 'transparent',
                        border: isMatch ? '1px solid rgba(0, 4, 60, 0.3)' : 'none',
                        fontWeight: isMatch ? 600 : 400,
                      }}
                    >
                      <Typography
                        component="span"
                        variant="caption"
                        noWrap
                        sx={{ flex: 1, mr: 1, fontFamily: 'monospace', fontSize: '0.7rem' }}
                      >
                        {col}
                        {isMatch && (
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ ml: 0.5, color: 'success.main', fontWeight: 600 }}
                          >
                            (matched)
                          </Typography>
                        )}
                      </Typography>
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ color: 'text.secondary', fontSize: '0.7rem' }}
                      >
                        {(Number(score) * 100).toFixed(1)}%
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-mapping-dialog-title"
        aria-describedby="delete-mapping-dialog-description"
        PaperProps={{
          sx: { borderRadius: 2, minWidth: 320 },
        }}
      >
        <DialogTitle id="delete-mapping-dialog-title">Delete mapping?</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-mapping-dialog-description">
            This will remove the mapping{' '}
            {hasMultipleSources ? (
              <>
                from <strong>{sourceColumns.join(', ')}</strong> to <strong>{targetColumn}</strong>
              </>
            ) : (
              <>
                between <strong>{leftKey}</strong> and <strong>{rightKey}</strong> ({left} ↔ {right})
              </>
            )}
            . This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
          <Button onClick={handleDeleteCancel} color="inherit" sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            sx={{ textTransform: 'none' }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {status === 'review' && sectionKey === 'no_mapping' && (
        <Button
          size="small"
          variant="outlined"
          disabled={disabled}
          onClick={() => setDeleteDialogOpen(true)}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            borderColor: '#dc2626',
            color: '#dc2626',
            bgcolor: '#fef2f2',
            '&:hover': {
              bgcolor: '#fee2e2',
              borderColor: '#dc2626',
            },
            fontSize: '0.75rem',
            px: 2,
            py: 0.5,
          }}
        >
          Delete
        </Button>
      )}
      {status === 'review' && sectionKey !== 'no_mapping' && (
        <Button
          size="small"
          variant="outlined"
          disabled={disabled}
          onClick={() => onOpenReview?.(mapping, editIndex)}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            borderColor: '#2563eb',
            color: '#2563eb',
            bgcolor: '#e0f2fe',
            '&:hover': {
              bgcolor: '#bfdbfe',
              borderColor: '#2563eb',
            },
            fontSize: '0.75rem',
            px: 2,
            py: 0.5,
          }}
        >
          REVIEW
        </Button>
      )}
    </Paper>
  );
};

export default JoinCard;
